import { useEffect, useState, useContext } from "react";
import axios from "axios";
import { OrganizationContext } from "../../clients/utils/OrganizationContext";

const API = "https://api.komorotel.com/api";

const euros = (n) => `${Math.round(n || 0).toLocaleString("fr-FR")} €`;

const jour = (d) => new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "short" });

/* Sous la date : le délai quand le départ est proche, le jour de la semaine
   quand il est lointain. À trois jours on veut savoir qu'il reste trois
   jours ; à six semaines on repère un séjour par son jour. */
const sousDate = (d) => {
  const j0 = new Date(); j0.setHours(0, 0, 0, 0);
  const cible = new Date(d); cible.setHours(0, 0, 0, 0);
  const jours = Math.round((cible - j0) / 86400000);

  if (jours <= 0) return "aujourd'hui";
  if (jours === 1) return "demain";
  if (jours <= 7)  return `dans ${jours} jours`;
  return new Date(d).toLocaleDateString("fr-FR", { weekday: "long" });
};

const MODE_TEXTE = {
  centralized:    "Vos membres ne réservent pas eux-mêmes.",
  always_approve: "Chaque réservation d'un membre passe par vous.",
  mission:        "Vos membres réservent librement dans l'enveloppe de leur mission.",
};

/* Le sujet de cet espace, ce sont des gens qui partent sur une île à une
   date. La page s'ouvre donc sur les départs, pas sur une grille de
   compteurs : c'est ce qu'un responsable vient voir en premier. Les
   totaux ferment la page, en retrait. */
export default function OrgDashboard({ setActivePage }) {
  const { organization } = useContext(OrganizationContext);
  const [members, setMembers] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/organization/members`,      { withCredentials: true }),
      axios.get(`${API}/organization/reservations`, { withCredentials: true }),
    ])
      .then(([m, r]) => { setMembers(m.data); setReservations(r.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const actifs   = members.filter(m => m.status === "active").length;
  const aValider = reservations.filter(r => r.organization?.approvalStatus === "pending");

  const debutMois = new Date();
  debutMois.setDate(1);
  debutMois.setHours(0, 0, 0, 0);

  const duMois = reservations.filter(
    r => r.status !== "cancelled" && new Date(r.checkInDate) >= debutMois
  );
  const totalMois = duMois.reduce((s, r) => s + (r.totalPrice  || 0), 0);
  const nuitsMois = duMois.reduce((s, r) => s + (r.totalNights || 0), 0);

  // Aucun membre et aucune réservation : le compte vient d'être ouvert.
  const vierge = members.length === 0 && reservations.length === 0;

  const departs = reservations
    .filter(r => ["confirmed", "awaiting_deposit"].includes(r.status) && new Date(r.checkInDate) >= new Date())
    .sort((a, b) => new Date(a.checkInDate) - new Date(b.checkInDate))
    .slice(0, 6);

  const nom = (r) =>
    r.organization?.snapshot?.travellerName
    || `${r.guest?.firstName || ""} ${r.guest?.lastName || ""}`.trim()
    || "Voyageur";

  return (
    <div className="px-5 sm:px-8 lg:px-12 py-8 sm:py-10 max-w-[980px]">

      <header className="mb-8 sm:mb-10">
        <h1 className="text-[23px] sm:text-[27px] leading-tight font-semibold tracking-[-0.01em] text-[var(--org-ink)]">
          {organization?.name || "Votre organisation"}
        </h1>
        <p className="text-[13.5px] sm:text-[13.5px] text-[var(--org-slate)] mt-1">
          {MODE_TEXTE[organization?.memberBookingMode] || ""}
        </p>
      </header>

      {/* L'ambre ne sert qu'ici. Un filet épais plutôt qu'une carte :
          ce n'est pas un encart, c'est une interruption. */}
      {aValider.length > 0 && (
        <section
          className="border-l-[3px] pl-4 sm:pl-5 py-1 mb-8 sm:mb-10"
          style={{ borderColor: "var(--org-sun)" }}
        >
          <p className="text-[16px] sm:text-[17px] text-[var(--org-ink)] leading-snug">
            {aValider.length} réservation{aValider.length > 1 ? "s" : ""} attend
            {aValider.length > 1 ? "ent" : ""} votre décision
          </p>
          <p className="text-[13.5px] text-[var(--org-slate)] mt-0.5">
            Les dates sont bloquées en attendant. Sans réponse, elles repartent à la vente.
          </p>
          <button
            onClick={() => setActivePage("reservations")}
            className="mt-3 px-3.5 py-[7px] rounded-lg text-[13.5px] font-semibold text-white transition hover:opacity-90"
            style={{ background: "var(--org-forest)" }}
          >
            Trancher maintenant
          </button>
        </section>
      )}

      {/* Un compte neuf ne voit pas le squelette d'une liste vide : il voit
          quoi faire. Les filets et les compteurs n'arrivent qu'avec les
          données qu'ils sont censés porter. */}
      {!loading && vierge ? (
        <section className="max-w-xl">
          <h2 className="text-[17px] font-semibold text-[var(--org-ink)]">Premiers pas</h2>
          <p className="text-[13.5px] sm:text-[13.5px] text-[var(--org-slate)] mt-1.5">
            Trois étapes, et vos équipes peuvent partir.
          </p>

          <ol className="mt-6 flex flex-col">
            {[
              {
                t: "Ajoutez les personnes que vous envoyez en mission",
                d: "Un nom et un email suffisent. Elles n'ont pas de compte à créer et n'ont rien à faire de leur côté.",
                action: { l: "Ajouter un membre", p: "members" },
              },
              {
                t: "Réservez leur séjour",
                d: "Choisissez l'hébergement sur komorotel.com et indiquez pour qui vous réservez. Le nom du voyageur part chez l'établissement, avec la mention de votre prise en charge.",
              },
              {
                t: "Recevez un relevé chaque mois",
                d: "Tous les séjours du mois sur une seule facture à votre nom, avec le détail par personne.",
              },
            ].map((e, i) => (
              <li
                key={e.t}
                className="flex gap-4 py-5"
                style={i > 0 ? { borderTop: "1px solid var(--org-line)" } : undefined}
              >
                <span
                  className="org-num shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[12px] font-semibold"
                  style={{ background: "var(--org-forest)", color: "#fff" }}
                >
                  {i + 1}
                </span>
                <div>
                  <p className="text-[14px] text-[var(--org-ink)]">{e.t}</p>
                  <p className="text-[13.5px] text-[var(--org-slate)] mt-1 leading-relaxed">{e.d}</p>
                  {e.action && (
                    <button
                      onClick={() => setActivePage(e.action.p)}
                      className="mt-3 px-3.5 py-[7px] rounded-lg text-[13.5px] font-semibold text-white transition hover:opacity-90"
                      style={{ background: "var(--org-forest)" }}
                    >
                      {e.action.l}
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ol>
        </section>
      ) : (
      <section>
        <h2 className="text-[17px] font-semibold text-[var(--org-ink)] pb-3">
          Prochains départs
        </h2>

        <div style={{ borderTop: "1px solid var(--org-line)" }}>
          {loading ? (
            [...Array(3)].map((_, i) => (
              <div key={i} className="h-[72px]" style={{ borderBottom: "1px solid var(--org-line)" }}>
                <div className="h-4 w-48 bg-gray-100 rounded mt-6 ml-20 animate-pulse" />
              </div>
            ))
          ) : departs.length === 0 ? (
            <div className="py-8">
              <p className="text-[14px] text-[var(--org-ink)]">Personne ne part pour l'instant.</p>
              <p className="text-[13.5px] text-[var(--org-slate)] mt-1 max-w-md">
                Ajoutez les personnes que vous envoyez en mission, puis réservez
                leur séjour. Elles n'ont pas de compte à créer.
              </p>
              <button
                onClick={() => setActivePage("members")}
                className="mt-4 px-3.5 py-[7px] rounded-lg text-[13.5px] font-semibold text-white transition hover:opacity-90"
                style={{ background: "var(--org-forest)" }}
              >
                Ajouter un membre
              </button>
            </div>
          ) : (
            departs.map(r => (
              <button
                key={r._id}
                onClick={() => setActivePage("reservations")}
                className="org-row w-full text-left py-4 transition hover:bg-white"
                style={{ borderBottom: "1px solid var(--org-line)" }}
              >
                {/* Téléphone : trois lignes pleine largeur. Une colonne date
                    fixe mangerait un cinquième de l'écran et écraserait les
                    noms d'hébergements, qui sont longs ici. */}
                <div className="sm:hidden">
                  <div className="flex items-baseline justify-between gap-3">
                    <p className="org-num text-[15px] font-medium text-[var(--org-ink)]">
                      {jour(r.checkInDate)}
                      <span className="ml-2 text-[12px] font-normal text-[var(--org-slate)]">
                        {sousDate(r.checkInDate)}
                      </span>
                    </p>
                    <p className="org-num text-[15px] font-medium text-[var(--org-ink)] shrink-0">
                      {euros(r.totalPrice)}
                    </p>
                  </div>
                  <p className="text-[16px] text-[var(--org-ink)] mt-1.5 truncate">{nom(r)}</p>
                  <div className="flex items-baseline justify-between gap-3 mt-0.5">
                    <p className="text-[13px] text-[var(--org-slate)] truncate">
                      {r.hotelId?.name || "Hébergement"}
                      {r.hotelId?.city ? `, ${r.hotelId.city}` : ""}
                    </p>
                    <p className="text-[13px] text-[var(--org-slate)] shrink-0">
                      {r.totalNights} nuit{r.totalNights > 1 ? "s" : ""}
                    </p>
                  </div>
                  {r.status === "awaiting_deposit" && (
                    <p className="text-[12.5px] mt-1.5" style={{ color: "#A5660B" }}>
                      Règlement attendu par l'établissement
                    </p>
                  )}
                </div>

                {/* Écran large : colonne date, comme un tableau de départs */}
                <div className="hidden sm:flex items-baseline gap-7">
                  <div className="w-[92px] shrink-0">
                    <p className="org-num text-[16px] font-medium text-[var(--org-ink)] leading-none">
                      {jour(r.checkInDate)}
                    </p>
                    <p className="text-[11.5px] text-[var(--org-slate)] mt-1 truncate">
                      {sousDate(r.checkInDate)}
                    </p>
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className="text-[14px] text-[var(--org-ink)] truncate">{nom(r)}</p>
                    <p className="text-[13px] text-[var(--org-slate)] truncate mt-0.5">
                      {r.hotelId?.name || "Hébergement"}
                      {r.hotelId?.city ? `, ${r.hotelId.city}` : ""}
                    </p>
                    {r.status === "awaiting_deposit" && (
                      <p className="text-[12.5px] mt-1" style={{ color: "#A5660B" }}>
                        Règlement attendu par l'établissement
                      </p>
                    )}
                  </div>

                  <div className="text-right shrink-0">
                    <p className="org-num text-[14px] text-[var(--org-ink)]">{euros(r.totalPrice)}</p>
                    <p className="text-[13px] text-[var(--org-slate)] mt-0.5">
                      {r.totalNights} nuit{r.totalNights > 1 ? "s" : ""}
                    </p>
                  </div>
                </div>
              </button>
            ))
          )}
        </div>

        {departs.length > 0 && (
          <button
            onClick={() => setActivePage("reservations")}
            className="mt-4 text-[13.5px] text-[var(--org-slate)] hover:text-[var(--org-ink)] transition"
          >
            Voir toutes les réservations
          </button>
        )}
      </section>
      )}

      {/* ── Totaux, en retrait. Séparés par des filets, pas par des cartes. ── */}
      {!loading && !vierge && (
      <section className="mt-10 sm:mt-14 pt-6" style={{ borderTop: "1px solid var(--org-line)" }}>
        <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-y-6">
          {[
            { v: actifs,          l: actifs > 1 ? "membres actifs" : "membre actif", page: "members" },
            { v: nuitsMois,       l: "nuits ce mois",   page: "reservations" },
            { v: duMois.length,   l: duMois.length > 1 ? "séjours ce mois" : "séjour ce mois", page: "reservations" },
            { v: euros(totalMois), l: "engagés ce mois", page: "statements" },
          ].map((t, i) => (
            <button
              key={t.l}
              onClick={() => setActivePage(t.page)}
              className={`text-left py-1 group pr-6 sm:pr-14 ${i > 0 ? "sm:border-l sm:pl-10" : ""}`}
              style={{ borderColor: "var(--org-line)" }}
            >
              <p className="org-num text-[22px] sm:text-[25px] font-semibold text-[var(--org-ink)] leading-none group-hover:opacity-70 transition">
                {loading ? "—" : t.v}
              </p>
              <p className="text-[13px] text-[var(--org-slate)] mt-1.5">{t.l}</p>
            </button>
          ))}
        </div>
      </section>
      )}
    </div>
  );
}
